import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  clientApiService,
  ChangeOrderListItem,
  ProjectItem,
} from "../../services/client-api.service";

const STATUS_LABELS: Record<string, { label: string; className: string }> = {
  pending: { label: "⏳ Pendiente de tu aprobación", className: "bg-yellow-100 text-yellow-700" },
  client_approved: { label: "✅ Aprobado por ti", className: "bg-green-100 text-green-700" },
  approved: { label: "✅ Aprobado", className: "bg-green-100 text-green-700" },
  rejected: { label: "❌ Rechazado", className: "bg-red-100 text-red-700" },
  executed: { label: "🔨 Ejecutado", className: "bg-blue-100 text-blue-700" },
  draft: { label: "Borrador", className: "bg-slate-100 text-slate-600" },
};

const CATEGORY_LABELS: Record<string, string> = {
  structural: "Estructural",
  finishes: "Acabados",
  systems: "Sistemas",
  layout: "Distribución",
  other: "Otro",
};

const PRIORITY_LABELS: Record<string, { label: string; className: string }> = {
  low: { label: "Baja", className: "text-slate-500" },
  medium: { label: "Media", className: "text-blue-600" },
  high: { label: "Alta", className: "text-orange-600" },
  critical: { label: "Crítica", className: "text-red-600" },
};

function formatCurrency(n: number) {
  return new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    minimumFractionDigits: 2,
  }).format(n);
}

function formatDate(t: number) {
  return new Date(t).toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export function ChangesListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const projectIdParam = searchParams.get("projectId") || "";

  const [changes, setChanges] = useState<ChangeOrderListItem[]>([]);
  const [projects, setProjects] = useState<ProjectItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");

  const load = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [changesData, projectsData] = await Promise.all([
        clientApiService.listChangeOrders(projectIdParam || undefined),
        clientApiService.listProjects(),
      ]);
      setChanges(changesData);
      setProjects(projectsData);
    } catch (err: any) {
      setError(err?.message || "Error al cargar cambios");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectIdParam]);

  const filtered = useMemo(() => {
    return changes.filter((c) => {
      const matchesStatus = statusFilter === "all" || c.status === statusFilter;
      const term = search.trim().toLowerCase();
      const matchesSearch =
        !term ||
        (c.number || "").toLowerCase().includes(term) ||
        (c.title || "").toLowerCase().includes(term) ||
        c.description.toLowerCase().includes(term) ||
        c.projectName.toLowerCase().includes(term);
      return matchesStatus && matchesSearch;
    });
  }, [changes, statusFilter, search]);

  const handleProjectChange = (id: string) => {
    const p = new URLSearchParams(searchParams);
    if (id) p.set("projectId", id);
    else p.delete("projectId");
    setSearchParams(p);
  };

  const pendingCount = changes.filter((c) => c.status === "pending").length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Órdenes de Cambio</h2>
          <p className="text-sm text-slate-500 mt-1">
            {changes.length} {changes.length === 1 ? "orden" : "órdenes"}
            {pendingCount > 0 && (
              <span className="ml-2 text-yellow-600 font-medium">
                • {pendingCount} pendiente{pendingCount !== 1 ? "s" : ""} de tu aprobación
              </span>
            )}
          </p>
        </div>
        <button
          onClick={load}
          disabled={isLoading}
          className="text-sm bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 px-4 py-2 rounded-lg font-medium transition disabled:opacity-50"
        >
          🔄 Actualizar
        </button>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          type="text"
          placeholder="Buscar por número, título o proyecto..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 px-4 py-2.5 border border-slate-200 rounded-lg bg-white focus:border-primary focus:ring-2 focus:ring-primary/15 outline-none text-sm"
        />
        <select
          value={projectIdParam}
          onChange={(e) => handleProjectChange(e.target.value)}
          className="px-4 py-2.5 border border-slate-200 rounded-lg bg-white focus:border-primary outline-none text-sm"
        >
          <option value="">Todos los proyectos</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2.5 border border-slate-200 rounded-lg bg-white focus:border-primary outline-none text-sm"
        >
          <option value="all">Todos los estados</option>
          <option value="pending">Pendientes de aprobación</option>
          <option value="client_approved">Aprobados por mí</option>
          <option value="approved">Aprobados por el despacho</option>
          <option value="rejected">Rechazados</option>
          <option value="executed">Ejecutados</option>
        </select>
      </div>

      {/* Lista */}
      {isLoading && changes.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4">
          {error}
          <button onClick={load} className="ml-3 underline font-medium">
            Reintentar
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center">
          <div className="text-5xl mb-3 opacity-40">🔄</div>
          <h3 className="text-lg font-semibold text-slate-900">
            {changes.length === 0 ? "Sin órdenes de cambio" : "Sin resultados"}
          </h3>
          <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
            {changes.length === 0
              ? "Cuando el despacho proponga modificaciones a tu obra, aparecerán aquí para tu revisión."
              : "Prueba con otros filtros."}
          </p>
        </div>
      ) : (
        <ul className="space-y-3">
          {filtered.map((c) => {
            const status = STATUS_LABELS[c.status] || {
              label: c.status,
              className: "bg-slate-100 text-slate-700",
            };
            const priority = PRIORITY_LABELS[c.priority] || PRIORITY_LABELS.medium;
            const isPending = c.status === "pending";
            return (
              <li key={c.id}>
                <Link
                  to={`/dashboard/changes/${c.id}`}
                  className={`block bg-white rounded-2xl border p-5 hover:shadow-md transition ${
                    isPending ? "border-yellow-300" : "border-slate-200 hover:border-primary/40"
                  }`}
                >
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1.5">
                        {c.number && (
                          <span className="text-xs font-mono bg-slate-100 text-slate-700 px-2 py-0.5 rounded">
                            {c.number}
                          </span>
                        )}
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${status.className}`}>
                          {status.label}
                        </span>
                        <span className={`text-xs font-medium ${priority.className}`}>
                          {priority.label}
                        </span>
                      </div>
                      <p className="font-semibold text-slate-900 truncate">
                        {c.title || "Cambio sin título"}
                      </p>
                      <p className="text-xs text-primary font-medium mt-0.5">
                        {c.projectName}
                      </p>
                      <p className="text-sm text-slate-500 mt-1 line-clamp-2">
                        {c.description}
                      </p>
                      <div className="flex items-center gap-3 text-xs text-slate-400 mt-2">
                        <span>{CATEGORY_LABELS[c.category] || c.category}</span>
                        <span>•</span>
                        <span>{formatDate(c.createdAt)}</span>
                        {c.requestedByName && (
                          <>
                            <span>•</span>
                            <span>Por {c.requestedByName}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-xs text-slate-500">Impacto</p>
                      <p
                        className={`text-lg font-bold ${
                          c.costImpact >= 0 ? "text-primary" : "text-green-600"
                        }`}
                      >
                        {c.costImpact >= 0 ? "+" : ""}
                        {formatCurrency(c.costImpact)}
                      </p>
                    </div>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}